// src/components/Vid.tsx
import React from 'react';
import styled from 'styled-components';
import preventDefaultEvents from '../hoc/preventDefaultEvents';
import {
  SECTION_ONE_VIDEO_URL,
  POSTER_URL,
} from '@/constants/core';
import {
  BRAND_TITLE_SHORT,
} from '@/i18n/locales/i18n.he';
const VideoWrapper = styled.div`
  border-radius: 20px;
  overflow: hidden;
  position: relative;
`;

const VideoContainer = styled.div`
  border: 2px solid transparent;
  border-image: linear-gradient(
    160deg,
    ${({ theme }) => theme.gradientColors[0]},
    ${({ theme }) => theme.gradientColors[1]}
  );
  border-image-slice: 1;
  overflow: hidden;
  position: relative;

  video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    background: #000;
    cursor: pointer; /* ⬅ מאפשר קליק לגיטימי */
  }
`;

const PlayButton = styled.button`
  position: absolute;
  inset: 0;
  margin: auto;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  font-size: 0;
  line-height: 0;
  display: grid;
  place-items: center;
  cursor: pointer;
  z-index: 5;
  transition: 0.2s ease;

  &::before {
    content: '';
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-left: 16px solid currentColor;
    transform: translateX(2px);
  }

  &:hover {
    background: rgba(0, 0, 0, 0.55);
    transform: scale(1.08);
  }
`;

const ClickOverlay = styled.div`
  position: absolute;
  inset: 0;
  cursor: pointer;
  z-index: 4;
`;


type VidProps = {
  title?: string;
};

const VideoPlayerBase: React.FC<VidProps> = ({ title }) => {
  const titleAttr = `${title} – וידאו`;
  const aria = `נגן וידאו – ${BRAND_TITLE_SHORT}`;

  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [isSrcLoaded, setIsSrcLoaded] = React.useState(false);
  const [isPlaying, setIsPlaying] = React.useState(false);

  React.useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setIsSrcLoaded(true);
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsSrcLoaded(true);
          obs.disconnect();
        }
      },
      { threshold: 0.25 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const playVideo = () => {
    if (!videoRef.current) return;

    videoRef.current
      .play()
      .then(() => {
        setIsPlaying(true);
      })
      .catch(() => {
      });
  };

  return (
    <VideoWrapper>
      <VideoContainer>
        {!isPlaying && (
          <PlayButton type="button" onClick={playVideo} aria-label={aria} />
        )}

        {!isPlaying && <ClickOverlay onClick={playVideo} />}

        <video
          ref={videoRef}
          controls={isPlaying}
          preload="none"
          playsInline
          aria-label={aria}
          title={titleAttr}
          poster={POSTER_URL}
          width={1280}
          height={720}
          onPlay={() => setIsPlaying(true)}
        >
          {isSrcLoaded && (
            <source src={SECTION_ONE_VIDEO_URL} type="video/mp4" />
          )}
          הדפדפן שלך אינו תומך בוידאו.
          <a href={SECTION_ONE_VIDEO_URL}>פתחו את הסרטון</a>
        </video>

        <noscript>
          <p>
            לצפייה בוידאו:{' '}
            <a href={SECTION_ONE_VIDEO_URL}>פתחו את הסרטון</a>
          </p>
        </noscript>
      </VideoContainer>
    </VideoWrapper>
  );
};

const Vid = React.memo(VideoPlayerBase);
export default preventDefaultEvents(Vid);
